import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import Header from "../components/Header";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";

export default function PaymentSuccess() {
  const { locale } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();

  const depositId =
    params.get("depositId") || localStorage.getItem("lexhab_pending_deposit");

  const [state, setState] = useState("pending"); // pending | completed | failed | timeout
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!depositId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);

    const poll = async () => {
      try {
        const { data } = await publicApi.paymentStatus(depositId);
        if (cancelled) return;
        if (data.status === "COMPLETED" && data.access_token) {
          localStorage.setItem("lexhab_sub_token", data.access_token);
          localStorage.setItem(
            "lexhab_sub_plan",
            data.plan + "/" + data.billing
          );
          // Store subscriber info for dashboard
          if (data.email) {
            localStorage.setItem(
              "lexhab_sub_info",
              JSON.stringify({
                email: data.email,
                plan: data.plan,
                billing_cycle: data.billing,
                expires_at: data.expires_at || null
              })
            );
          }
          localStorage.removeItem("lexhab_pending_deposit");
          setState("completed");
          if (pollRef.current) clearInterval(pollRef.current);
          if (timerRef.current) clearInterval(timerRef.current);
          setTimeout(() => nav("/account"), 3000);
          return;
        }
        if (data.status === "FAILED" || data.status === "REJECTED") {
          setState("failed");
          if (pollRef.current) clearInterval(pollRef.current);
          if (timerRef.current) clearInterval(timerRef.current);
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          setState("failed");
          if (pollRef.current) clearInterval(pollRef.current);
        }
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    // Timeout after 90 seconds
    const timeoutHandle = setTimeout(() => {
      if (!cancelled && state === "pending") {
        setState("timeout");
        if (pollRef.current) clearInterval(pollRef.current);
        if (timerRef.current) clearInterval(timerRef.current);
      }
    }, 90000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      clearTimeout(timeoutHandle);
    };
    // eslint-disable-next-line
  }, [depositId]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="payment-success-page">
      <Header />
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-lg bg-white border border-primary/15 rounded-sm shadow-lg p-8">
          {state === "pending" && <Pending elapsed={elapsed} locale={locale} />}
          {state === "completed" && <Completed locale={locale} />}
          {state === "failed" && <Failed locale={locale} onRetry={() => nav("/chat")} />}
          {state === "timeout" && <Timeout locale={locale} onRetry={() => nav("/chat")} />}
        </div>
      </div>
    </div>
  );
}

function Pending({ elapsed, locale }) {
  return (
    <div className="text-center" data-testid="payment-pending">
      <div className="w-14 h-14 mx-auto bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-5">
        <Loader2 size={24} className="animate-spin" />
      </div>
      <h1 className="font-display text-2xl font-semibold text-primary mb-2">
        {locale === "fr" ? "Paiement en cours de confirmation…" : "Confirming payment…"}
      </h1>
      <p className="text-sm text-foreground/60">
        {locale === "fr"
          ? "Validez la transaction depuis votre téléphone (MTN ou Orange) si cela vous est demandé."
          : "Approve the transaction from your phone (MTN or Orange) if prompted."}
      </p>
      <div className="mt-6 font-mono text-[11px] uppercase tracking-widest text-foreground/40">
        {elapsed}s · {locale === "fr" ? "vérification toutes les 3s" : "polling every 3s"}
      </div>
    </div>
  );
}

function Completed({ locale }) {
  return (
    <div className="text-center" data-testid="payment-completed">
      <div className="w-14 h-14 mx-auto bg-[#D1FAE5] text-[#065F46] rounded-sm flex items-center justify-center mb-5">
        <CheckCircle2 size={28} />
      </div>
      <h1 className="font-display text-2xl font-semibold text-primary mb-2">
        {locale === "fr" ? "Paiement confirmé 🎉" : "Payment confirmed 🎉"}
      </h1>
      <p className="text-sm text-foreground/70 leading-relaxed">
        {locale === "fr"
          ? "Votre abonnement est actif. Vous pouvez désormais poser des questions illimitées."
          : "Your subscription is active. You can now ask unlimited questions."}
      </p>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-foreground/40">
        {locale === "fr" ? "Redirection automatique vers le chat…" : "Redirecting to chat…"}
      </p>
    </div>
  );
}

function Failed({ locale, onRetry }) {
  return (
    <div className="text-center" data-testid="payment-failed">
      <div className="w-14 h-14 mx-auto bg-[#FEE2E2] text-[#991B1B] rounded-sm flex items-center justify-center mb-5">
        <AlertTriangle size={24} />
      </div>
      <h1 className="font-display text-2xl font-semibold text-[#991B1B] mb-2">
        {locale === "fr" ? "Paiement non abouti" : "Payment failed"}
      </h1>
      <p className="text-sm text-foreground/70 leading-relaxed">
        {locale === "fr"
          ? "La transaction a été annulée ou refusée. Aucun montant ne vous a été débité."
          : "The transaction was cancelled or declined. No amount has been charged."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
        data-testid="payment-retry-btn"
      >
        {locale === "fr" ? "Retour au chat" : "Back to chat"}
      </button>
    </div>
  );
}

function Timeout({ locale, onRetry }) {
  return (
    <div className="text-center" data-testid="payment-timeout">
      <div className="w-14 h-14 mx-auto bg-[#FEF3C7] text-[#92400E] rounded-sm flex items-center justify-center mb-5">
        <Clock size={24} />
      </div>
      <h1 className="font-display text-2xl font-semibold text-[#92400E] mb-2">
        {locale === "fr" ? "Délai dépassé" : "Timeout"}
      </h1>
      <p className="text-sm text-foreground/70 leading-relaxed">
        {locale === "fr"
          ? "Nous n'avons pas reçu de confirmation. Si vous avez bien été débité, contactez support@lexhab.cm — votre paiement sera activé manuellement."
          : "We didn't receive confirmation. If you were charged, contact support@lexhab.cm — we'll activate your subscription manually."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-5 inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
      >
        {locale === "fr" ? "Retour au chat" : "Back to chat"}
      </button>
    </div>
  );
}
